import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  useTheme,
  useMediaQuery,
  TextField,
  Box,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import {
  loginRequest,
  b2cMsalConfig,
  b2cLoginRequest,
  b2cPasswordResetRequest,
  b2cMicrosoftLoginRequest,
  b2cGoogleLoginRequest,
  b2cEmailLoginRequest,
  b2cPhoneLoginRequest,
  apiScopes,
} from "../conf/authConfig";
import PersonIcon from "@mui/icons-material/Person";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import GoogleIcon from "@mui/icons-material/Google";
import MicrosoftIcon from "@mui/icons-material/Microsoft";
import PhoneIcon from "@mui/icons-material/Phone";
import { useDualMsal } from "../contexts/MsalContext";

const b2cClientId = "2bf86a50-1598-4a57-9c3c-f736585b94f2";
const entraClientId = "76aa34c3-0e31-4f5b-be1b-ee769da13dd2";

export default function LoginDialog({ open, onClose }) {
  const location = useLocation();
  const { b2c, entra } = useDualMsal();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [role, setRole] = React.useState("participant");
  const [email, setEmail] = React.useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Effect to clear MSAL interaction status when dialog opens and trigger warmup
  React.useEffect(() => {
    if (open) {
      console.log("Login dialog opened, clearing MSAL interaction status...");
      const b2cInteractionStatusKey = `msal.${b2cClientId}.interaction.status`;
      const entraInteractionStatusKey = `msal.${entraClientId}.interaction.status`;
      sessionStorage.removeItem(b2cInteractionStatusKey);
      sessionStorage.removeItem(entraInteractionStatusKey);
      console.log(
        "Cleared keys:",
        b2cInteractionStatusKey,
        entraInteractionStatusKey
      );
      // Fire-and-forget warmup call so the function worker and Cosmos connection
      // are initialised before the user finishes authenticating
      fetch("/api/warmup").catch(() => {});
    }
  }, [open]); // Run when the 'open' prop changes

  const handleRoleChange = (event, newRole) => {
    if (newRole !== null) {
      setRole(newRole);
      setError(null);
    }
  };

  // Handle URL params and location state
  React.useEffect(() => {
    // Get email from URL if provided
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get("email");
    if (emailParam) {
      setEmail(decodeURIComponent(emailParam));
    }
  }, [location, onClose]);

  const handleLogin = async (provider = null) => {
    setLoading(true);
    setError(null);

    try {
      if (role === "participant") {
        // Determine login config based on provider
        let loginConfig;

        if (provider === "microsoft.com") {
          console.log("Using Microsoft login for B2C");
          loginConfig = {
            ...b2cMicrosoftLoginRequest,
          };

          // Only add login hint if we have an email
          if (email) {
            loginConfig.loginHint = email;
          }

          // Log the final config to help troubleshoot
          console.log(
            "Microsoft login config:",
            JSON.stringify({
              ...loginConfig,
              extraQueryParameters: {
                ...loginConfig.extraQueryParameters,
              },
            })
          );
        } else if (provider === "google.com") {
          console.log("Using Google login for B2C");
          loginConfig = {
            ...b2cGoogleLoginRequest,
          };

          // Only add login hint if we have an email
          if (email) {
            loginConfig.loginHint = email;
          }
        } else if (provider === "phone") {
          console.log("Using Phone login for B2C");
          loginConfig = {
            ...b2cPhoneLoginRequest,
          };

          // Phone authentication does not use email hint
        } else {
          // Default email login
          console.log("Using email login for B2C");
          loginConfig = {
            ...b2cEmailLoginRequest,
          };

          // Add login_hint but avoid setting domain_hint for Safari compatibility
          if (email) {
            loginConfig.extraQueryParameters = {
              ...loginConfig.extraQueryParameters,
              login_hint: email,
            };

            // Only add domain_hint if not Safari
            const isSafari = /^((?!chrome|android).)*safari/i.test(
              navigator.userAgent
            );
            if (!isSafari && email.includes("@")) {
              loginConfig.extraQueryParameters.domain_hint = email
                .split("@")[1]
                .toLowerCase();
            }
          }
        }

        console.log(
          "Starting B2C login with config:",
          JSON.stringify(loginConfig, null, 2)
        );
        // Close dialog before redirect
        onClose();

        await b2c.loginRedirect(loginConfig);
        console.log("B2C login redirect initiated");
      } else {
        // Use Entra ID for admin/PI
        console.log("Starting Entra ID login for admin/PI");

        // Create login configuration
        const loginConfig = {
          ...loginRequest,
        };

        // Add login hint if email is provided
        if (email) {
          loginConfig.loginHint = email;
        }

        // Close dialog before redirect
        onClose();

        await entra.loginRedirect(loginConfig);
        console.log("Entra ID login redirect initiated");
      }
    } catch (err) {
      console.error("Login error:", err);
      // Check for redirects which are expected and should not show as errors
      if (
        err.message &&
        (err.message.includes("redirect") ||
          err.message.includes("interaction_in_progress") ||
          err.errorCode === "user_cancelled")
      ) {
        console.log("Redirect initiated, this is normal behavior");
        return;
      }

      // For real errors, show to the user
      setError(err.message || "Failed to sign in. Please try again.");
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    try {
      console.log("Starting password reset flow");
      const resetConfig = {
        ...b2cPasswordResetRequest,
      };

      // Add login hint if email is provided
      if (email) {
        resetConfig.loginHint = email;
      }

      console.log(
        "Password reset config:",
        JSON.stringify(resetConfig, null, 2)
      );

      // Close dialog before redirect
      onClose();

      await b2c.loginRedirect(resetConfig);
      console.log("Password reset redirect initiated");
    } catch (err) {
      console.error("Password reset error:", err);
      // Check for redirects which are expected and should not show as errors
      if (
        err.message &&
        (err.message.includes("redirect") ||
          err.message.includes("interaction_in_progress") ||
          err.errorCode === "user_cancelled")
      ) {
        console.log("Redirect initiated, this is normal behavior");
        return;
      }

      // For real errors, show to the user
      setError("Failed to start password reset. Please try again.");
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ pt: 4, px: 3 }}>
        <Typography
          variant="h5"
          component="h2"
          gutterBottom
          align="center"
          sx={{ mb: 3 }}
        >
          Sign In to MyMeds
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <ToggleButtonGroup
            color="primary"
            value={role}
            exclusive
            onChange={handleRoleChange}
            aria-label="User Role"
            fullWidth
            sx={{ mb: 3 }}
          >
            <ToggleButton value="participant" aria-label="participant">
              <PersonIcon sx={{ mr: 1 }} />
              Participant
            </ToggleButton>
            <ToggleButton value="admin" aria-label="admin">
              <AdminPanelSettingsIcon sx={{ mr: 1 }} />
              Researcher / Admin
            </ToggleButton>
          </ToggleButtonGroup>

          <Stack spacing={2}>
            {role === "participant" ? (
              <>
                {/* Participant login options */}
                <TextField
                  label="Email"
                  variant="outlined"
                  fullWidth
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  sx={{ mb: 1 }}
                />

                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => handleLogin("email")}
                  disabled={loading}
                  sx={{ minHeight: "48px" }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Sign In with Email"
                  )}
                </Button>

                <Divider>OR</Divider>

                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<PhoneIcon />}
                  onClick={() => handleLogin("phone")}
                  disabled={loading}
                  sx={{ minHeight: "48px" }}
                >
                  Sign in with Phone
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<MicrosoftIcon />}
                  onClick={() => handleLogin("microsoft.com")}
                  disabled={loading}
                  sx={{ minHeight: "48px" }}
                >
                  Sign in with Microsoft Account
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<GoogleIcon />}
                  onClick={() => handleLogin("google.com")}
                  disabled={loading}
                  sx={{ minHeight: "48px" }}
                >
                  Sign in with Google
                </Button>

                <Button
                  fullWidth
                  variant="text"
                  onClick={handlePasswordReset}
                  disabled={loading}
                  sx={{ minHeight: "48px" }}
                >
                  Forgot Password?
                </Button>
              </>
            ) : (
              <>
                {/* Admin login options */}
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => handleLogin()}
                  disabled={loading}
                  sx={{ minHeight: "48px" }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Sign In with Microsoft Account"
                  )}
                </Button>

                <Typography
                  variant="body2"
                  align="center"
                  color="textSecondary"
                >
                  You'll be redirected to your organization's login.
                </Typography>
              </>
            )}
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={loading} variant="outlined">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
